import { getItemDefinition } from './catalog.js';
import { EQUIPMENT_SLOTS } from './types.js';
import { deepFreeze } from './validation.js';
import { inventoryMassGrams, validateRuntimeStack, isRuntimeInstanceId } from './inventory.js';
export const PHYSICAL_ACTIONS = ['QUICK', 'RETRIEVE', 'STOW', 'MOVE', 'BIND', 'TOGGLE_LIGHT', 'INSERT_BATTERY', 'REMOVE_BATTERY', 'CANCEL', 'EQUIP', 'UNEQUIP', 'SET_CLOSURE'];
export const PHYSICAL_RESULT_CODES = ['OK', 'BUSY', 'STALE_STATE', 'INVALID_REQUEST', 'INVALID_ITEM', 'INVALID_POCKET', 'INVALID_SLOT', 'HAND_OCCUPIED', 'NO_ROOM', 'NO_POWER', 'WRONG_BATTERY', 'GARMENT_NOT_EMPTY', 'POCKET_CLOSED', 'SWIMMING'];
export function equippedPockets(state) {
    return EQUIPMENT_SLOTS.flatMap(slot => {
        const garment = state.equipment[slot];
        const wearable = garment && getItemDefinition(garment.definitionId)?.wearable;
        if (!garment?.garment || !wearable)
            return [];
        return wearable.pockets.flatMap(definition => {
            const pocket = garment.garment.pockets.find(p => p.id === definition.id);
            return pocket ? [{ ref: { garmentInstanceId: garment.instanceId, pocketId: pocket.id }, definition, state: pocket, garment }] : [];
        });
    });
}
export function findEquippedPocket(state, ref) {
    return equippedPockets(state).find(p => samePocket(p.ref, ref));
}
export function samePocket(a, b) {
    return !!a && !!b && a.garmentInstanceId === b.garmentInstanceId && a.pocketId === b.pocketId;
}
export function stackVolumeMl(item) { return (getItemDefinition(item.definitionId)?.volumeMl ?? Infinity) * item.quantity; }
export function pocketVolumeMl(pocket) { return pocket.items.reduce((sum, item) => sum + stackVolumeMl(item), 0); }
export function pocketFits(pocket, item) {
    const def = getItemDefinition(item.definitionId);
    return !!def && !def.wearable && def.longestDimensionMm <= pocket.definition.maxDimensionMm
        && (!pocket.definition.allowedCategories || pocket.definition.allowedCategories.includes(def.category))
        && pocketVolumeMl(pocket.state) + stackVolumeMl(item) <= pocket.definition.capacityMl + 1e-9;
}
export function pocketAccessSeconds(state, ref, itemInstanceId) {
    const pocket = findEquippedPocket(state, ref);
    if (!pocket)
        return Infinity;
    const def = pocket.definition;
    const fill = Math.min(1, pocketVolumeMl(pocket.state) / def.capacityMl);
    const index = itemInstanceId ? pocket.state.items.findIndex(item => item.instanceId === itemInstanceId) : pocket.state.items.length - 1;
    const above = index < 0 ? 0 : pocket.state.items.slice(index + 1).reduce((sum, item) => sum + stackVolumeMl(item), 0) / def.capacityMl;
    const neighbor = def.obstructedByPocketId ? equippedPockets(state).find(p => p.ref.garmentInstanceId === ref.garmentInstanceId && p.ref.pocketId === def.obstructedByPocketId) : undefined;
    const obstruction = neighbor ? Math.min(1, pocketVolumeMl(neighbor.state) / neighbor.definition.capacityMl) : 0;
    const closure = def.requiresOpenClosure && !pocket.garment.garment?.closures[def.requiresOpenClosure]
        ? getItemDefinition(pocket.garment.definitionId)?.wearable?.closures.find(c => c.id === def.requiresOpenClosure)?.openSeconds ?? 0 : 0;
    return Math.min(30, def.baseAccessSeconds + def.fillPenaltySeconds * fill * fill + def.burialPenaltySeconds * above + (def.obstructionPenaltySeconds ?? 0) * obstruction * obstruction + closure);
}
export function lightBatteryFraction(item) {
    const config = getItemDefinition(item.definitionId)?.lightDevice?.battery;
    if (!config || item.device?.cells.length !== config.count)
        return 0;
    return Math.min(...item.device.cells.map(cell => cell?.battery && getItemDefinition(cell.definitionId)?.battery?.format === config.format ? cell.battery.remainingWh / cell.battery.capacityWh : 0));
}
export function equipmentVisualState(state) {
    const garments = {};
    for (const slot of EQUIPMENT_SLOTS) {
        const item = state.equipment[slot];
        const visual = item && getItemDefinition(item.definitionId)?.wearable?.visualId;
        if (visual)
            garments[slot] = visual;
    }
    const held = state.hands.MAIN_HAND?.item;
    return { garments, mainHand: held ? { definitionId: held.definitionId, lightOn: !!held.device?.on } : null };
}
const projectionCache = new WeakMap();
/** Compatibility transport only: capacity decisions always use physical pockets. */
export function physicalInventoryProjection(state) {
    const cached = projectionCache.get(state);
    if (cached)
        return cached;
    const slots = equippedPockets(state).flatMap(p => [...p.state.items]);
    if (state.hands.MAIN_HAND)
        slots.push(state.hands.MAIN_HAND.item);
    if (!slots.length)
        slots.push(null);
    const projection = deepFreeze({ inventoryId: `player:${state.playerId}`, revision: state.revision, slotCount: slots.length, maxMassGrams: null, massGrams: inventoryMassGrams(slots), slots });
    projectionCache.set(state, projection);
    return projection;
}
export { PHYSICAL_STARTER_LOADOUT } from './definitions/clothing.js';
/** Checked once on authoritative sync, never on the render path. */
export function validatePhysicalCarryState(value) {
    if (!value || typeof value !== 'object')
        return false;
    const state = value;
    try {
        if (!isRuntimeInstanceId(state.playerId) || !Number.isSafeInteger(state.revision) || state.revision < 0 || !state.equipment || !state.hands || !state.autoNight
            || typeof state.autoNight.night !== 'boolean' || typeof state.autoNight.suppressed !== 'boolean'
            || (state.autoNight.automaticItemId !== null && !isRuntimeInstanceId(state.autoNight.automaticItemId)))
            return false;
        const ids = new Set();
        for (const [slot, item] of Object.entries(state.equipment))
            if (!EQUIPMENT_SLOTS.includes(slot) || !item || getItemDefinition(item.definitionId)?.wearable?.equipmentSlot !== slot || !validateRuntimeStack(item, 0, ids))
                return false;
        for (const hand of [state.hands.MAIN_HAND, state.hands.OFF_HAND]) {
            if (hand === null)
                continue;
            if (!hand || !validateRuntimeStack(hand.item, 0, ids))
                return false;
            if (hand.origin !== null && (!hand.origin || !findEquippedPocket(state, hand.origin)))
                return false;
        }
        for (const pocket of equippedPockets(state))
            if (pocket.state.quickItemId && !pocket.state.items.some(item => item.instanceId === pocket.state.quickItemId)
                && !(state.hands.MAIN_HAND?.item.instanceId === pocket.state.quickItemId && samePocket(state.hands.MAIN_HAND.origin, pocket.ref)))
                return false;
        const action = state.action;
        if (action !== null) {
            if (!action || !isRuntimeInstanceId(action.id) || !isRuntimeInstanceId(action.itemInstanceId) || !['RETRIEVE', 'STOW', 'MOVE', 'INSERT_BATTERY', 'REMOVE_BATTERY'].includes(action.type)
                || !Number.isFinite(action.startedAt) || !Number.isFinite(action.completesAt) || action.completesAt <= action.startedAt || action.completesAt - action.startedAt > 30000
                || action.expectedInventoryRevision !== state.revision || typeof action.automatic !== 'boolean')
                return false;
            for (const location of [action.source, action.destination])
                if (location !== 'MAIN_HAND' && (!location || !findEquippedPocket(state, location)))
                    return false;
        }
        return true;
    }
    catch {
        return false;
    }
}
export function garmentEffectiveInsulation(definition, state) {
    return definition.closures.reduce((insulation, closure) => insulation * (state.closures[closure.id] ? closure.openInsulationMultiplier : 1), definition.insulationClo);
}
